import type { ReactNode, JSX} from "react";
import { cn, gapClass, pxClass, pyClass, pClass } from "./cn";

export interface RowProps {
  /** Gap between children (Tailwind spacing scale) */
  readonly gap?: number;
  /** Uniform padding */
  readonly p?: number;
  /** Horizontal padding */
  readonly px?: number;
  /** Vertical padding */
  readonly py?: number;
  /** Cross-axis alignment */
  readonly align?: "start" | "center" | "end" | "stretch" | "baseline";
  /** Main-axis justification */
  readonly justify?: "start" | "center" | "end" | "between";
  /** Take remaining flex space */
  readonly flex1?: boolean;
  /** Truncate overflowing content */
  readonly truncate?: boolean;
  /** Allow wrapping */
  readonly wrap?: boolean;
  /** Extra className (positioning, margins only) */
  readonly className?: string;
  readonly children: ReactNode;
}

const alignMap: Readonly<Record<string, string>> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline",
};

const justifyMap: Readonly<Record<string, string>> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
};

export function Row({
  gap,
  p,
  px,
  py,
  align = "center",
  justify,
  flex1,
  truncate,
  wrap,
  className,
  children,
}: RowProps): JSX.Element {
  return (
    <div
      className={cn(
        "flex",
        gapClass(gap),
        pClass(p),
        pxClass(px),
        pyClass(py),
        alignMap[align],
        justify && justifyMap[justify],
        flex1 && "flex-1 min-w-0",
        truncate && "min-w-0",
        wrap && "flex-wrap",
        className,
      )}
    >
      {children}
    </div>
  );
}
